'use client'
import { useMemo } from 'react'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import type { ContentAlignment, ContentWidth } from '@/components/GridLayout/GridLayout.types'
import { type SbBaseBlockProps, type StoryblokTableField } from '@/services/storyblok/storyblok'
import { useFormatComparisonValue } from '@/utils/useFormatComparisonValue'
import { storyblokEditable } from '@storyblok/react'
import {
  type ComparisonTableBody,
  type ComparisonTableProps,
} from 'ui/src/components/ComparisonTable/ComparisonTable.types'
import { DesktopComparisonTable } from 'ui/src/components/ComparisonTable/DesktopComparisonTable'
import { MobileComparisonTable } from 'ui/src/components/ComparisonTable/MobileComparisonTable'
import { desktopOnly, mobileOnly } from 'ui/src/utils/visibility.css'

type Props = SbBaseBlockProps<{
  table: StoryblokTableField
  layout?: {
    widths: ContentWidth
    alignment: ContentAlignment
  }
  caption?: string
}>

export const ComparisonTableBlock = ({ blok, nested }: Props) => {
  const formatValue = useFormatComparisonValue()

  const table = useMemo<ComparisonTableProps>(() => {
    const head = blok.table.thead.map((item) => item.value)
    // We currently don't support descriptions for first column items in storyblok
    // so bellow we're just formatting the data accordingly to match 'Table' type
    const body: ComparisonTableBody = blok.table.tbody.map((row) => {
      const [{ value: rowTitle }, ...remainingValues] = row.body
      const values = remainingValues.map(({ value }) => formatValue(value))
      return [{ title: rowTitle }, ...values]
    })

    return {
      head,
      body,
    }
  }, [formatValue, blok])

  return (
    <GridLayout.Root nested={nested} {...storyblokEditable(blok)}>
      <GridLayout.Content width={blok.layout?.widths ?? { _: '1' }} align={blok.layout?.alignment ?? 'center'}>
        {table.head.length > 2 ? (
          <>
            {/* Both tables are rendered in the HTML simultaneously and toggled via CSS media queries.
            This avoids CLS and keeps table data visible to search engines, at the cost of a
            slightly larger HTML payload. */}
            <MobileComparisonTable className={mobileOnly} caption={blok.caption} {...table} />
            <DesktopComparisonTable className={desktopOnly} caption={blok.caption} {...table} />
          </>
        ) : (
          <DesktopComparisonTable caption={blok.caption} {...table} />
        )}
      </GridLayout.Content>
    </GridLayout.Root>
  )
}
